import { Ref, ref } from 'vue-demi';
import { defineStore } from 'pinia';
import { isEmpty } from 'lodash';

const DEFAULT_ACTIONS = ['getById', 'setById', 'setAll', 'hasValue'] as const;
type SchemaToType<S> = S extends undefined ? any : S extends { default: infer D }
  ? D
  : {
    [P in keyof S]: S[P] extends { default: infer D } ? D : never;
  };

type DefaultActionKeys = typeof DEFAULT_ACTIONS[number];
type DefinitionMethod<T, A extends any[], R> = (data: T, ...args: A) => R;
type DefinitionMethods<T> = Omit<{ [K: string]: DefinitionMethod<T, any[], any> }, DefaultActionKeys>;

type OperationMethods<T, SS extends DefinitionMethods<T>> = {
  [K in keyof SS]: SS[K] extends DefinitionMethod<T, infer A, infer R> ? (id: number, ...args: A) => R : never;
};

/**
 * 创建项目数据Store
 * @param name store名称
 * @param actions 可操作的方法 (data: S, ...args: any[]) => any
 * @param schema 项目数据结构(存储的每个项目的数据结构) data的类型 S
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createProjectDataStore = <SS extends DefinitionMethods<SchemaToType<S>>, S = any>(name: string, actions: SS, schema?: S) => {
  return defineStore(name, () => {
    const data = ref<Record<number, SchemaToType<S>>>({}) as Ref<Record<number, SchemaToType<S>>>;

    const getById = (id: number) => data.value[id];
    const setById = (id: number, value: SchemaToType<S>) => {
      data.value = {
        ...data.value,
        [id]: value
      };
    };
    const setAll = (value: Record<number, SchemaToType<S>>) => {
      data.value = value;
    };
    const hasValue = () => !isEmpty(data.value);

    const methods = {} as OperationMethods<SchemaToType<S>, SS>;
    const keys = Object.keys(actions).filter(k => !DEFAULT_ACTIONS.includes(k as DefaultActionKeys)) as (keyof typeof methods)[];
    for (const key of keys) {
      const method = actions[key];
      if (typeof method === 'function') {
        // 显式指定参数和返回类型
        methods[key] = ((id: number, ...args: any[]) => {
          return method(getById(id), ...args);
        }) as SS[typeof key] extends DefinitionMethod<SchemaToType<S>, infer A, infer R> ? (id: number, ...args: A) => R : never;
      }
    }

    return { getById, setById, setAll, hasValue, ...methods };
  });
};
